/**
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: WidgetConfig
 * Reason: Configures the WidgetFactory table widget of the account messages
 *   (bm.CustomerBillingAccountBillingRunMessage, section (4) below - QL entity table, current messages by
 *   default, rebuilt under its own alias for „Veraltete anzeigen") and renders the three read sections of
 *   the tab „Bankverbindungen & IBANs" of a bm.CustomerBillingAccount (round 11, WP-B/B1) -
 *   the three meanings of „IBAN am Konto" side by side and labelled (requirement 11 §7 rule 3),
 *   plus the account's SEPA pre-check messages:
 *     (1) Zeitscheiben   - the bm.CustomerBillingAccountBank rows of the account, oldest first, each
 *                          classified aktuell / geplant / vergangen FOR DISPLAY (E-2 - money flows keep
 *                          following the backend resolver); a row opens its cr.CustomerBankAccount;
 *     (2) Für Lastschriften verwendet - the debit IBANs actually collected from, aggregated CLIENT
 *                          side from the SEPA snapshots of a bounded read (FD-R11-10, 500 newest);
 *     (3) Zahler-IBANs   - the payer IBANs of incoming transfers (bm.CustomerIbanHistory): the former
 *                          „IBAN-Verlauf" table of the full view, moved here UNCHANGED;
 *     (4) Meldungen      - bm.CustomerBillingAccountBillingRunMessage of the account as a WidgetFactory
 *                          table (FD-R11-8), current messages by default, „Veraltete anzeigen" (E-3).
 *   Full IBAN everywhere (Q-11c). Every read is QL through the fe-data building blocks of
 *   bm-current-bank-account-ql.ts (E-1: no new endpoint). Loading / error / empty per section (H9).
 *
 *   „KEINE BANKVERBINDUNG" IS JUDGED BY PAYMENT METHOD (E-9, FD-R11-2): for a SEPA account without a
 *   slice valid today the section shows a WARNING with the direct entry into the bank-change wizard;
 *   for a SELF / standing-order account without slices a neutral hint. The one predicate is
 *   `isBmDirectDebitPaymentMethod` - never a second pair of ordinals here.
 *
 *   THE BANK-CHANGE WIZARD IS OPENED THROUGH A BROKER, NOT BY IMPORT: the wizard host is a
 *   feature-crm component and bm must not import feature-crm (FD-R11-7). See
 *   `BmBankChangeWizardRequestService` for the mechanism; without a registered host the button is
 *   DISABLED with the reason as tooltip, never a silent click.
 */
import {
    AfterViewChecked,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {Subscription} from 'rxjs';

import {QlService} from 'features/core/shared/ql/service/ql.service';
import {QlQueryResult} from 'features/core/shared/ql/builder/ql-query-builder';
import {MvsUiObjectService} from 'features/core/shared/object/service/mvs-ui-object.service';
import {MvsCrudModeEnum} from 'features/core/shared/service/crud/mvs-crud-mode.enum';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {EntityStatusEnum} from 'features/core/shared/entity/enum/entity-status.enum';

import {CustomerIbanHistoryDto} from '../../../model/dto/entity/customer-billing-account.dto';
import {
    BM_ACCOUNT_BANK_SLICES_QUERY,
    BM_ACCOUNT_ERROR_MESSAGE_COUNT_QUERY,
    BM_ACCOUNT_SEPA_DEBIT_IBANS_QUERY,
    BM_SEPA_DEBIT_IBANS_PAGE_SIZE,
    bmAccountErrorMessageCountQuery,
    bmBankSlicesQuery,
    bmFindCurrentBankSlice,
    bmGroupSepaDebitIbans,
    bmNormalizeIban,
    bmNotObsoleteMessageFilter,
    bmReadAccountErrorMessageCounts,
    bmReadBankSlices,
    bmSepaDebitIbansQuery,
    bmTodayIsoDate
} from '../../../logic/current-bank-account/bm-current-bank-account-ql';
import type {BmBankSliceRow} from '../../../logic/current-bank-account/bm-bank-slice-row';
import type {BmSepaDebitIbanGroup} from '../../../logic/current-bank-account/bm-sepa-debit-iban-group';
import type {BmBankSliceView} from '../../../interface/bm-bank-slice-view.interface';
import type {BmSepaDebitIbanView} from '../../../interface/bm-sepa-debit-iban-view.interface';
import type {
    BmBillingAccountDetailSection
} from '../bm-customer-billing-accounts-overview/bm-billing-account-section-selection.interface';
import {
    BM_PAYMENT_METHOD_LABELS,
    isBmDirectDebitPaymentMethod,
    resolveBmPaymentEnumLabel
} from '../../../logic/payment-enum-labels/bm-payment-enum-labels';
import {
    BM_BANK_CHANGE_WIZARD_UNAVAILABLE_TEXT,
    BmBankChangeWizardRequestService
} from '../../../service/domain/bank-change-wizard-request/bm-bank-change-wizard-request.service';

/** Object type alias of the bank account a slice row opens. */
const CR_CUSTOMER_BANK_ACCOUNT_OBJECT_TYPE = 'cr.CustomerBankAccount';

/** Entity alias behind the messages widget (registered as a full entity service by fe-data, A3). */
const BM_ACCOUNT_MESSAGE_OBJECT_TYPE = 'bm.CustomerBillingAccountBillingRunMessage';

/** How many run names the „Läufe" column prints before it shortens to „…". */
const BM_DEBIT_RUN_NAMES_SHOWN = 3;

const TOOLTIP_BANK_CHANGE_WIZARD =
    'Bankwechsel-Assistent öffnen – die heute gültige Bankverbindung ist als zu ersetzende vorausgewählt';

@Component({
    selector: 'bm-customer-account-bank-connections',
    templateUrl: './bm-customer-account-bank-connections.component.html',
    styleUrls: ['./bm-customer-account-bank-connections.component.scss'],
    standalone: false
})
export class BmCustomerAccountBankConnectionsComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {

    /** The bm.CustomerBillingAccount whose bank connections are shown. */
    @Input() accountId: number;

    /**
     * Round 11 (E-4): the section the opener asked for - „n SEPA-Meldungen" on an overview card deep-links
     * to `'messages'`. That section is highlighted and scrolled into view once it is rendered; null
     * means no request. Set by the full view from the import context's `linkedAttribute`.
     */
    @Input() focusSection: BmBillingAccountDetailSection | null = null;

    /** The messages section's element - the scroll target of a `'messages'` focus. */
    @ViewChild('messagesSection') messagesSection: ElementRef<HTMLElement>;

    /** True from a focus request until the section was scrolled into view once (it may not be rendered yet). */
    private focusScrollPending: boolean = false;

    /** The account's customer - the context the bank-change wizard runs in. Without it no wizard. */
    @Input() customerId: number | null;

    /** Raw bm.PaymentMethod ordinal of the account - decides warning vs neutral hint (E-9). */
    @Input() paymentMethod: number | null;

    /** The payer IBANs of incoming transfers, as the account read hydrates them (section 3). */
    @Input() ibanHistory: CustomerIbanHistoryDto[] | null;

    /**
     * The bank-change wizard opened from THIS component has ended - the host should reload the
     * account header (the wizard may have written a new slice).
     */
    @Output() onBankChangeWizardClosed = new EventEmitter<void>();

    initialized: boolean = false;

    /** True while the QL batch of the three read sections is in flight. */
    loading: boolean = false;

    /** True when the last batch FAILED - the sections say so instead of rendering as empty. */
    loadError: boolean = false;

    // ---- (1) Zeitscheiben ----
    bankSlices: BmBankSliceRow[] = [];
    sliceRows: BmBankSliceView[] = [];
    currentBankSlice: BmBankSliceRow | null = null;

    // ---- (2) Für Lastschriften verwendet ----
    sepaDebitRows: BmSepaDebitIbanView[] = [];
    /** True when the bounded read came back full - the footer states the bound (FD-R11-10). */
    sepaDebitPageFull: boolean = false;
    readonly sepaDebitPageSize: number = BM_SEPA_DEBIT_IBANS_PAGE_SIZE;

    // ---- (4) Meldungen ----
    /** Current (not obsolete) ERROR messages - the number the section header states (E-4). */
    currentErrorMessageCount: number = 0;
    includeObsoleteMessages: boolean = false;
    messagesWidget: WidgetData | null = null;

    /** Skeleton rows while loading - a stable array, so the @for never re-renders on CD. */
    readonly skeletonRows: number[] = [1, 2, 3];

    /** The form of the slices pipe - the source of the EnumInfo labels (R-U4). */
    private sliceForm: any = null;

    private loadSubscription: Subscription | null = null;
    private wizardClosedSubscription: Subscription | null = null;

    /** True between our own wizard request and its close notification. */
    private wizardRequestPending: boolean = false;

    constructor(
        protected qlService: QlService,
        protected objectService: MvsUiObjectService,
        protected bankChangeWizardRequestService: BmBankChangeWizardRequestService) {
    }

    ngOnInit(): void {
        this.wizardClosedSubscription = this.bankChangeWizardRequestService.closed$.subscribe(() => {
            if (!this.wizardRequestPending) {
                return;
            }
            this.wizardRequestPending = false;
            this.reload();
            this.onBankChangeWizardClosed.emit();
        });
        this.initComponent();
    }

    initComponent(): void {
        this.focusScrollPending = this.focusSection != null;
        this.refreshComponent();
        this.initialized = true;
    }

    refreshComponent(): void {
        this.buildMessagesWidget();
        this.load();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['focusSection']) {
            this.focusScrollPending = this.focusSection != null;
        }
        if (!this.initialized) {
            return;
        }
        if (changes['accountId']) {
            this.includeObsoleteMessages = false;
            this.refreshComponent();
        }
    }

    /**
     * Scrolls the requested section into view ONCE, as soon as its element exists - the section may
     * be rendered only after the parent switched tabs, which is why this waits for the view rather
     * than acting in ngOnChanges. A cheap flag test on every pass; nothing else happens here.
     */
    ngAfterViewChecked(): void {
        if (!this.focusScrollPending) {
            return;
        }
        const element: HTMLElement | undefined = this.focusSection === 'messages'
            ? this.messagesSection?.nativeElement
            : undefined;
        if (!element) {
            return;
        }
        this.focusScrollPending = false;
        element.scrollIntoView({behavior: 'smooth', block: 'start'});
    }

    /** Whether a section is the one the opener asked for - it carries a primary border while it is. */
    isSectionFocused(section: BmBillingAccountDetailSection): boolean {
        return this.focusSection === section;
    }

    ngOnDestroy(): void {
        this.loadSubscription?.unsubscribe();
        this.wizardClosedSubscription?.unsubscribe();
    }

    /** Re-read everything (retry after an error, after a wizard run, after a bank account was edited). */
    reload(): void {
        this.refreshComponent();
    }

    // ------------------------------------------------------------------
    // Loading
    // ------------------------------------------------------------------

    /**
     * ONE QL batch: the slices (with `includeForm`, so the mandate and entity status of a bank account
     * are labelled from the backend EnumInfo, R-U4), the bounded debit read, and the ONE-row error
     * COUNT of the messages section (the table itself is the widget; nothing reads the messages twice).
     * An answer that arrives after the account changed is discarded - it belongs to another account.
     */
    private load(): void {

        this.loadSubscription?.unsubscribe();
        this.loadError = false;

        const accountId: number = this.accountId;

        if (accountId == null) {
            this.applyResult(null);
            this.loading = false;
            return;
        }

        this.loading = true;

        this.loadSubscription = this.qlService.builder()
            .query(bmBankSlicesQuery(accountId))
            .query(bmSepaDebitIbansQuery(accountId))
            .query(bmAccountErrorMessageCountQuery(accountId))
            .pipe({
                name: BM_ACCOUNT_BANK_SLICES_QUERY,
                simpleQuery: BM_ACCOUNT_BANK_SLICES_QUERY,
                settings: {includeForm: true}
            })
            .pipe({name: BM_ACCOUNT_SEPA_DEBIT_IBANS_QUERY, simpleQuery: BM_ACCOUNT_SEPA_DEBIT_IBANS_QUERY})
            .pipe({name: BM_ACCOUNT_ERROR_MESSAGE_COUNT_QUERY, simpleQuery: BM_ACCOUNT_ERROR_MESSAGE_COUNT_QUERY})
            .execute()
            .subscribe({
                next: (result: QlQueryResult) => {
                    if (!this.isDisplayedAccount(accountId)) {
                        return;
                    }
                    this.applyResult(result);
                    this.loading = false;
                },
                error: () => {
                    if (!this.isDisplayedAccount(accountId)) {
                        return;
                    }
                    this.applyResult(null);
                    this.loadError = true;
                    this.loading = false;
                }
            });
    }

    private isDisplayedAccount(accountId: number): boolean {
        return this.accountId != null && Number(this.accountId) === Number(accountId);
    }

    /** Folds the batch answer into the view rows of the sections; `null` clears them. */
    private applyResult(result: QlQueryResult | null): void {

        const todayIso: string = bmTodayIsoDate();

        this.sliceForm = result?.getForm(BM_ACCOUNT_BANK_SLICES_QUERY) ?? null;
        this.bankSlices = result ? bmReadBankSlices(result.get(BM_ACCOUNT_BANK_SLICES_QUERY), todayIso) : [];
        this.currentBankSlice = bmFindCurrentBankSlice(this.bankSlices);
        this.sliceRows = this.bankSlices.map(slice => this.toSliceView(slice));

        const debitRows: Record<string, any>[] = result ? result.get(BM_ACCOUNT_SEPA_DEBIT_IBANS_QUERY) : [];
        this.sepaDebitPageFull = debitRows.length >= BM_SEPA_DEBIT_IBANS_PAGE_SIZE;
        this.sepaDebitRows = bmGroupSepaDebitIbans(debitRows).map(group => this.toDebitView(group));

        // The count query is filtered to THIS account, so every row of the answer is ours - summing the
        // map (rather than looking one id up) also survives an id that arrives as a string.
        this.currentErrorMessageCount = 0;
        if (result) {
            bmReadAccountErrorMessageCounts(result.get(BM_ACCOUNT_ERROR_MESSAGE_COUNT_QUERY))
                .forEach(count => this.currentErrorMessageCount += count);
        }
    }

    private toSliceView(slice: BmBankSliceRow): BmBankSliceView {
        // The bank account's entity status is worth a tag only when it is NOT the normal „aktiv" - a
        // slice pointing at a deactivated / deleted bank account is the fact a clerk must see.
        const bankInactive: boolean = slice.bankEntityStatus != null
            && slice.bankEntityStatus !== EntityStatusEnum.active;
        return {
            slice: slice,
            mandateStatusLabel: this.getEnumLabelFromForm('cba.sepaMandateStatus', slice.sepaMandateStatus),
            bankEntityStatusLabel: bankInactive
                ? this.getEnumLabelFromForm('cba.entityStatus', slice.bankEntityStatus)
                : null,
            stateLabel: BmCustomerAccountBankConnectionsComponent.sliceStateLabel(slice),
            stateSeverity: BmCustomerAccountBankConnectionsComponent.sliceStateSeverity(slice)
        };
    }

    /**
     * The bank of a debit IBAN is found BY IBAN EQUALITY against the account's slices - the snapshot
     * carries no bank-account FK (CANNOT 2). No match: the badge falls back to its initials / icon.
     */
    private toDebitView(group: BmSepaDebitIbanGroup): BmSepaDebitIbanView {

        const bank: BmBankSliceRow | null = group.iban == null
            ? null
            : (this.bankSlices.find(slice => bmNormalizeIban(slice.iban) === group.iban) ?? null);

        const names: string[] = group.billingRuns.map(run => run.name || ('Lauf #' + run.id));
        const shown: string[] = names.slice(0, BM_DEBIT_RUN_NAMES_SHOWN);

        return {
            group: group,
            bankImage: bank?.bankImage ?? null,
            bankName: bank?.bankName ?? null,
            bankBranchName: bank?.bankBranchName ?? null,
            runsText: shown.join(', ') + (names.length > BM_DEBIT_RUN_NAMES_SHOWN ? ' …' : ''),
            runsTooltip: names.join(', ')
        };
    }

    /**
     * The German label of an enum ordinal from the pipe's form metadata (backend EnumInfo) - the
     * SAME lookup the overview cards use. `null` when the form carries no value list for the field
     * or the value is unknown, so the template prints nothing rather than a raw ordinal.
     */
    private getEnumLabelFromForm(fieldName: string, value: number | null): string | null {
        if (value == null) {
            return null;
        }
        const entries: any[] = this.sliceForm?.formFields?.[fieldName]?.valueList?.entries;
        if (!Array.isArray(entries)) {
            return null;
        }
        const entry = entries.find((candidate: any) => candidate?.key === value);
        return entry?.label ?? null;
    }

    /** „Aktuell" / „Geplant ab dd.MM.yyyy" / „Vergangen" (E-2). */
    static sliceStateLabel(slice: BmBankSliceRow): string {
        switch (slice.state) {
            case 'current':
                return 'Aktuell';
            case 'planned':
                return 'Geplant ab ' + BmCustomerAccountBankConnectionsComponent.formatIsoDate(slice.startDate);
            default:
                return 'Vergangen';
        }
    }

    static sliceStateSeverity(slice: BmBankSliceRow): 'success' | 'info' | 'secondary' {
        switch (slice.state) {
            case 'current':
                return 'success';
            case 'planned':
                return 'info';
            default:
                return 'secondary';
        }
    }

    /** 'yyyy-MM-dd' -> 'dd.MM.yyyy' for a label; anything else is returned as it is. */
    static formatIsoDate(isoDate: string | null): string {
        if (!isoDate || !/^\d{4}-\d{2}-\d{2}/.test(isoDate)) {
            return isoDate ?? '';
        }
        return isoDate.substring(8, 10) + '.' + isoDate.substring(5, 7) + '.' + isoDate.substring(0, 4);
    }

    // ------------------------------------------------------------------
    // (1) Zeitscheiben - states and actions
    // ------------------------------------------------------------------

    /** The one E-9 predicate: a missing slice is a defect only for a direct-debit account. */
    isDirectDebit(): boolean {
        return isBmDirectDebitPaymentMethod(this.paymentMethod);
    }

    /** SEPA account without a slice valid today (E-9): warning with the direct entry into the wizard. */
    showMissingBankWarning(): boolean {
        return !this.loading && !this.loadError && this.isDirectDebit() && this.currentBankSlice == null;
    }

    /** Non-SEPA account without any slice (E-9): neutral, a bank connection is not required. */
    showNoBankRequiredHint(): boolean {
        return !this.loading && !this.loadError && !this.isDirectDebit() && this.bankSlices.length === 0;
    }

    /** Backend label of the account's payment method through the one FE helper (Q-11j). */
    getPaymentMethodLabel(): string {
        return resolveBmPaymentEnumLabel(BM_PAYMENT_METHOD_LABELS, this.paymentMethod);
    }

    /** A row opens its bank account in the generic object dialog (update mode -> the cr change OBC). */
    openBankAccount(slice: BmBankSliceRow, event?: Event): void {

        event?.stopPropagation();

        if (slice?.bankAccountId == null) {
            return;
        }

        this.objectService.openObjectViaDialog(
            null,
            CR_CUSTOMER_BANK_ACCOUNT_OBJECT_TYPE,
            slice.bankAccountId,
            null,
            false,
            false,
            () => this.reload(),
            MvsCrudModeEnum.update
        );
    }

    /** The wizard button is usable only with a customer AND a registered host (see the broker). */
    canOpenBankChangeWizard(): boolean {
        return this.customerId != null && this.bankChangeWizardRequestService.hasHost();
    }

    getBankChangeWizardTooltip(): string {
        return this.canOpenBankChangeWizard() ? TOOLTIP_BANK_CHANGE_WIZARD : BM_BANK_CHANGE_WIZARD_UNAVAILABLE_TEXT;
    }

    /**
     * „Bankverbindung ersetzen (Assistent)" (E-14): the bank of the slice valid today is preselected
     * as the one to replace; a SEPA account without a slice opens the wizard with nothing preselected.
     */
    openBankChangeWizard(): void {

        if (!this.canOpenBankChangeWizard()) {
            return;
        }

        const preselected: number[] = this.currentBankSlice?.bankAccountId != null
            ? [this.currentBankSlice.bankAccountId]
            : [];

        this.wizardRequestPending = this.bankChangeWizardRequestService.requestOpen({
            customerId: this.customerId,
            preselectedReplacedBankAccountIds: preselected,
            sourceBillingAccountId: this.accountId
        });
    }

    // ------------------------------------------------------------------
    // (4) Meldungen
    // ------------------------------------------------------------------

    /** (Re)builds the messages widget for the current account and „Veraltete anzeigen" state. */
    private buildMessagesWidget(): void {

        this.messagesWidget = null;

        if (this.accountId == null) {
            return;
        }

        const filters: FilterCriteria[] = [
            FilterCriteria.create('e.customerBillingAccountId', FilterCriteria.cOperatorEqual, this.accountId)
        ];

        if (!this.includeObsoleteMessages) {
            filters.push(bmNotObsoleteMessageFilter('e'));
        }

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Table of the bm.CustomerBillingAccountBillingRunMessage rows of THIS billing account
         *         (SEPA pre-check and future account message types), newest first, FK texts resolved so
         *         the run and the bank account („<Inhaber> – <IBAN> – <Bank>", full IBAN, FD-R11-4) are
         *         readable. By default only current messages (E-3 / E-4: `bmNotObsoleteMessageFilter`,
         *         the same predicate the run dashboard and the card badge use); „Veraltete anzeigen"
         *         rebuilds it without that filter under its own alias. Message types are labelled by
         *         the widget from the backend EnumInfo - no switch over `messageType` here (R-U4).
         */
        this.messagesWidget = WidgetFactory.createWidgetTableEntityQl(
            this.includeObsoleteMessages
                ? 'bm.customer.account.messages.table.all'
                : 'bm.customer.account.messages.table.current',
            this.includeObsoleteMessages ? 'Meldungen (inklusive veralteter)' : 'Meldungen',
            BM_ACCOUNT_MESSAGE_OBJECT_TYPE,
            'Keine Meldungen zu diesem Abrechnungskonto – die SEPA-Vorabprüfung hat nichts beanstandet.',
            filters,
            [new Sorting('e.createdDate', false), new Sorting('e.id', false)],
            true
        );
    }

    /** The toggle „Veraltete anzeigen" changed - PrimeNG has already written the model. */
    handleIncludeObsoleteChange(): void {
        this.buildMessagesWidget();
    }
}
